import moment from "moment";

import { AIScribeUserLinkStatus } from "@/AIScribe/Components/AIScribeUserLinkStatus";
import { IAIScribeContainerModel } from "@/AIScribe/Models/IAIScribeContainerModel";
import { EmployeeAsyncActions } from "@/Common/Api/AsyncActions/EmployeeAsyncActions";
import { Email } from "@/Common/Api/Contacts/Dto/Email";
import { ExternalBillingUserInfoDto } from "@/Common/Api/ExternalBilling/Dto/ExternalBillingUserInfoDto";
import { BaseReactComponent, EmptyProps } from "@/Common/Components/BaseReactComponent";
import { PharmaNetContext, PharmaNetCredentials } from "@/Common/Components/Billing/PharmaNetCredentials";
import { PrivateBillCostCenter } from "@/Common/Components/Billing/PrivateBillCostCenter";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { HyperLinkControl } from "@/Common/Components/Buttons/HyperLinkControl";
import { CallCenterSettings } from "@/Common/Components/CallCenter/CallCenterSettings";
import { FeatureFlagControl } from "@/Common/Components/FeatureFlagControl";
import { FileUploadLite } from "@/Common/Components/FileUpload/FileUploadLite";
import { Autocomplete } from "@/Common/Components/Inputs/Autocomplete";
import { ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { ComboBoxControl } from "@/Common/Components/Inputs/ComboboxControl";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { RadioButton } from "@/Common/Components/Inputs/RadioButton";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { LoadingIndicator } from "@/Common/Components/Loader/LoadingIndicator";
import { ChangePasswordPopupControl } from "@/Common/Components/Password/ChangePasswordPopupControl";
import { IUseEmailUpdateStateParams } from "@/Common/Components/RequiredRecoveryEmailFlow/Hooks/useEmailUpdateState";
import { RequiredRecoveryEmailController } from "@/Common/Components/RequiredRecoveryEmailFlow/RequiredRecoveryEmailController";
import { ClinicianSearchStrategy, PersonSearchAutocompleteCustom } from "@/Common/Components/Search/PersonSearchAutocompleteCustom";
import { PinControl } from "@/Common/Components/Sign/PinControl";
import { SignatureViewer } from "@/Common/Components/Sign/SignatureViewer";
import { SignatureViewerThreeToOneRatio } from "@/Common/Components/Sign/SignatureViewerThreeToOneRatio";
import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import { SignatureHelper } from "@/Common/Helpers/SignatureHelper";
import { PrescriptionPrintType } from "@/Common/Models/Employee/Enums/Settings";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { BillingProvider, BillingProviderHelper } from "@/Common/Services/ExternalBilling/BillingProvider";
import { ExternalBillingManager } from "@/Common/Services/ExternalBilling/ExternalBillingManager";
import { Dispatcher } from "@/Common/Stores/Dispatcher";
import { CheckboxLabel } from "@/DesignSystem";
// eslint-disable-next-line import/no-restricted-paths
import FlowsheetTemplateSettingsSectionController from "@/Flowsheets/Components/FlowsheetTemplateSettings/FlowsheetTemplateSettingsSectionController";
import { CertThumbprint } from "@/Settings/Api/Dto/Response/CertThumbprint";
import { ISettingsUIModel } from "@/Settings/Models/UI/ISettingsUIModel";
import { DrugInteractionAlertsFilterOption } from "../../Common/Models/Employee/Enums/DrugInteractionAlertsFilterOption";
import { OrphanLabsClinician } from "../Api/Dto/Response/OrphanLabsClinician";
import { IConfigModel } from "../Models/UI/IConfigModel";
import { AutoResponderForm } from "./AutoResponderForm";
import { AutoResponderView } from "./AutoResponderView";
import { FaxAccountNumbersControl } from "./FaxAccountNumbersControl";
import { MultifactorAuthenticationSettings } from "./MultifactorAuthenticationSettings";
import { NewFlowMultifactorAuthenticationSettings } from "./NewFlowMultifactorAuthenticationSettings";
import { PatientEnrollmentPopupControl } from "./PatientEnrollmentPopupControl";
import { PinFrequencyOptionViewer } from "./PinFrequencyOptionViewer";
import SettingsTabRow from "./SettingsTabRow";
import { SettingsTabSection } from "./SettingsTabSection";
import { UrlEdit } from "./UrlEdit";
import { ViewEmailAddressesControl } from "./ViewEmailAddressesControl";

import "./SettingsTab.css";
import styles from "./SettingsTab.module.scss";

import img_loading from "@/Globals/_images/loading.gif";

class SettingsTabState {
    public configuration: IConfigModel;
    public ui: ISettingsUIModel;
    public employee: EmployeeUIModel;
    public itemsOrphanLabs: Immutable.List<SearchableItemModelT<KeyValuePair<string, string>>>;
    public emailsToSave: Email[];
    public isNotifiedByEmail: boolean;
    public selectedBillingProviderCode: string;
    public showPharmaNetCredentials: boolean;
    public pharmaNetUsername: string;
    public externalBillingUser: ExternalBillingUserInfoDto;
    public longTermMedicationDurationInDays: number;
    public longTermMedicationDefaultRefillNumber: number;
    public showPasswordRecoveryPopup: boolean = false;
    public aiScribeModel: IAIScribeContainerModel;
}

class SettingsTab extends BaseReactComponent<EmptyProps, SettingsTabState> {
    private readonly defaultLongTermMedicationDurationInDays = 30;
    private readonly LongTermMedicationDefaultRefillNumber = 0;
    private readonly employeeActions = new EmployeeAsyncActions();

    private changeListenerMethodId: string;
    private aiscribeStoreListenerId: string;
    private clinicianSearchStrategy: ClinicianSearchStrategy;

    public componentWillMount(): void {
        const cliniciansWithoutCurrentUser = cacheStore.CliniciansAndEmployeeGroups.filter(x => x.ContactFacetId != sessionStore.getCurrentEmployee().ContactFacetId);
        this.clinicianSearchStrategy = new ClinicianSearchStrategy(cliniciansWithoutCurrentUser);

        let cliniciansAsKVPList = settingsStore.configuration.get("CliniciansAsKVPList").filter(x => x.Key != sessionStore.getCurrentEmployee().ContactId);

        const initialState = new SettingsTabState();
        initialState.configuration = settingsStore.configuration;
        initialState.ui = settingsStore.uiLogic;
        initialState.employee = sessionStore.getCurrentEmployee();
        initialState.itemsOrphanLabs = Immutable.List<SearchableItemModelT<KeyValuePair<string, string>>>(cliniciansAsKVPList.map(x => new SearchableItemModelT<KeyValuePair<string, string>>(x.Value, x.Key)));
        initialState.emailsToSave = [];
        initialState.longTermMedicationDurationInDays = settingsStore.configuration.get("LongTermMedicationDurationInDays") || this.defaultLongTermMedicationDurationInDays;
        initialState.longTermMedicationDefaultRefillNumber = settingsStore.configuration.get("LongTermMedicationDefaultRefillNumber") || this.LongTermMedicationDefaultRefillNumber;

        const currentEmployee = sessionStore.getCurrentEmployee();
        initialState.selectedBillingProviderCode = EnumHelper.getName(BillingProvider, currentEmployee.getBillingProvider());

        initialState.aiScribeModel = aiScribeStore.Model;

        this.state = initialState;
    }

    public componentDidMount(): void {
        $(window).trigger("resize");

        this.changeListenerMethodId = settingsStore.AddChangeListener(this.changeListener, this, "settings-listener");
        this.aiscribeStoreListenerId = aiScribeStore.AddChangeListener(this.onAIScribeStoreChange, this, "aiscribe-listener");

        sessionStore.dispatch(LocalOperations.Settings.LoadSharingConfiguration);
        sessionStore.dispatch(LocalOperations.Settings.LoadOrphanLabsForwards);
        sessionStore.dispatch(LocalOperations.Settings.LoadEmployeeEmails);
        sessionStore.dispatch(LocalOperations.Settings.RefreshSettings);

        const currentEmployee = sessionStore.getCurrentEmployee();
        currentEmployee.getExternalBillingUserAsync()
            .then(user => this.mutateState(s => s.externalBillingUser = user));
    }

    private onAIScribeStoreChange(): void {
        this.mutateState(x => x.aiScribeModel = aiScribeStore.Model);
    }

    public componentDidUpdate(): void {
        $(window).trigger("resize");
    }

    public componentWillUnmount(): void {
        settingsStore.RemoveChangeListener(this.changeListenerMethodId);
        aiScribeStore.RemoveChangeListener(this.aiscribeStoreListenerId);
    }

    public shouldComponentUpdate(nextProps, nextState): boolean {
        return (
            this.state.configuration !== nextState.configuration
            || this.state.ui !== nextState.ui
            || this.state.employee !== nextState.Employee   // Refactoring and testing are required to replace nextState.Employee by nextState.employee
        );
    }

    public render(): JSX.Element {
        let enrollmentPopup: JSX.Element = null;
        if (this.state.ui.get("DisplayEnrollmentSummary")) {
            enrollmentPopup = <PatientEnrollmentPopupControl Model={this.state.configuration} />;
        }

        let locateDSQThumbPrintPopup: JSX.Element = null;
        if (this.state.ui.get("DisplayLocatedDSQCertificates") == true) {
            let certsRender: JSX.Element[] = [];
            let certs = this.state.ui.get("LocatedDSQCertificates");
            certs.forEach((cert: CertThumbprint, idx) => {
                certsRender.push(
                    (
                        <div key={"cert_" + idx} className={styles.certificatesThumbPrintContainer}>
                            <HyperLinkControl operation={LocalOperations.SettingsUI.SelectDSQKey} item={cert}>
                                <div className={`flex-container ${styles.certificatesThumbPrintValue}`}>
                                    <div className={styles.certificatesThumbPrintSubValue}>
                                        <span>{cert.Thumbprint.substring(0, 5)}</span>
                                    </div>
                                    <div>
                                        <span>{" - " + cert.CertificateName}</span>
                                    </div>
                                </div>
                            </HyperLinkControl>
                        </div>
                    )
                );
            });

            if (certs != null) {
                locateDSQThumbPrintPopup = (
                    <div className={`popup ${styles.thumbPrintDSQContainer}`}>
                        <div className="row p-header">
                            <div className="col-lg-12  col-md-12 col-sm-12">
                                <HyperLinkControl className="close" aria-label="Close" operation={LocalOperations.SettingsUI.CloseLocateDSQKeysPopup} text="X" />
                                <h4 className="uppercase">{cacheStore.GetString("DSQThumbprintOpenWindowTitle")}</h4>
                            </div>
                        </div>
                        <div className="p-body">
                            <div className="row">
                                <div className="col-lg-12">
                                    <div className={styles.thumbPrintDSQValue}>
                                        <div>
                                            {certsRender}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            }
        }

        return (
            <div className="sub-container">

                {this.showOverlay() && (
                    <div className="global-overlay translucent" />
                )}

                <div className="left-nav-menu settings transition">
                    <div className="ws-scrollable hide-overflow" />
                </div>
                <div className="workspace">

                    {this.state.ui.get("ShowPwdChange") === true && (
                        <ChangePasswordPopupControl
                            popupTitle="ChangePasswordLabel"
                            isAdminReset={false}
                            userHasAdminPermission={sessionStore.isAdmin()}
                            onClosePopupCallBack={() => Dispatcher.dispatch(LocalOperations.SettingsUI.HidePwdChange)}
                            onSuccess={() => Dispatcher.dispatch(LocalOperations.SettingsUI.HidePwdChange)}
                            customPasswordRuleMessage={sessionStore.customPasswordRuleMessage}
                            customAdminPasswordRuleMessage={sessionStore.customAdminPasswordRuleMessage}
                            customPasswordValidationRegex={sessionStore.customPasswordValidationRegex}
                        />
                    )}

                    <div className="myle-page-header bottom-border">
                        <div className="row">
                            <div className="col-lg-12 col-md-12 col-sm-12">
                                <h3 className="thin-font">{cacheStore.GetString("SettingsLabel")}</h3>
                            </div>
                        </div>
                    </div>
                    <div id="posref" className="ws-scrollable">
                        <div className="myle-doc-tb main-content myle-form">
                            {this.renderGeneralSettings()}
                            {this.renderYourPreferences()}
                            {this.renderParentAndResultsManagement()}
                            {this.renderCommunicationSection()}
                            {this.renderSecuritySection()}
                            {this.renderOtherSection()}
                        </div>
                    </div>
                    <div className="ws-footer collapsed" />
                </div>

                {enrollmentPopup}
                {locateDSQThumbPrintPopup}
                {this.renderPharmaNetCredentialsPopup()}
            </div>
        );
    }

    private renderGeneralSettings(): JSX.Element {
        let billingProvidersItems = cacheStore.ClinicSettings.EnableNewFmpBillingIntegration
            ? [
                this.getBillingComboBoxDropDownItem(BillingProvider.Medfar, 0),
                this.getBillingComboBoxDropDownItem(BillingProvider.FacNet, 1),
                this.getBillingComboBoxDropDownItem(BillingProvider.Xacte, 2),
                this.getBillingComboBoxDropDownItem(BillingProvider.XacteMultiD, 3),
                this.getBillingComboBoxDropDownItem(BillingProvider.Fmp, 4),
                this.getBillingComboBoxDropDownItem(BillingProvider.Nestor, 5),
                this.getBillingComboBoxDropDownItem(BillingProvider.ClinicDefault, 6),
                this.getBillingComboBoxDropDownItem(BillingProvider.SoftInfo, 7)
            ]
            : [
                this.getBillingComboBoxDropDownItem(BillingProvider.Fmp, 0),
                this.getBillingComboBoxDropDownItem(BillingProvider.FacNet, 1),
                this.getBillingComboBoxDropDownItem(BillingProvider.Xacte, 2),
                this.getBillingComboBoxDropDownItem(BillingProvider.XacteMultiD, 3),
                this.getBillingComboBoxDropDownItem(BillingProvider.Nestor, 4),
                this.getBillingComboBoxDropDownItem(BillingProvider.ClinicDefault, 5),
                this.getBillingComboBoxDropDownItem(BillingProvider.SoftInfo, 6)
            ];

        if (cacheStore.ClinicSettings.EnableClinicAidBilling)
            billingProvidersItems.push(this.getBillingComboBoxDropDownItem(BillingProvider.ClinicAid, billingProvidersItems.length));

        return (
            <SettingsTabSection
                labelKey="SettingsTabSectionTitle_GeneralSettings"
                dataCy="settingsTab-generalSettingsSection"
            >
                <div className="row">
                    <div className="hidden-sm hidden-xs col-md-2 col-lg-2 text-right">
                        <label className="form-label bold">{cacheStore.GetString("LanguagesLabel")}</label>
                    </div>
                    <div className="col-sm-12 col-md-10 col-lg-10">
                        <label className="visible-xs bold visible-sm">{cacheStore.GetString("LanguagesLabel")}</label>
                        <div className={styles.radioButtonWidth}>
                            <RadioButton
                                mainDivCSS="no-side-margin"
                                items={this.state.configuration.get("LanguageOptions")}
                                radioInputGroupId="language"
                                onePerLine={false}
                                changeOperation={LocalOperations.Settings.LanguageChange}
                                selectedItem={this.state.configuration.get("Language")}

                                // DV : Do not comment out this condition to force Spanish On. The database MUST have proper lookups for spanish mode to work correctly. Fix the database by injecting to proper missing lookups, don't hack the code.
                                // PL : https://www.youtube.com/watch?v=u3CKgkyc7Qo
                                disableItem={item => (item.Value === "es-MX" && !cacheStore.HasESLookups) || (item.Value === "fr" && !cacheStore.HasFRLookups)}
                            />
                        </div>
                    </div>
                </div>

                {!this.state.employee.isClerical() && (
                    <div className="row">
                        <div className="hidden-sm hidden-xs col-md-2 col-lg-2 text-right">
                            <label className="form-label bold">{cacheStore.GetString("DefaultMeasurementSystem")}</label>
                        </div>
                        <div className="col-sm-12 col-md-10 col-lg-10">
                            <label className="visible-xs bold visible-sm">{cacheStore.GetString("DefaultMeasurementSystem")}</label>

                            <div className={styles.radioButtonWidth}>
                                <RadioButton
                                    mainDivCSS="no-side-margin"
                                    items={this.state.configuration.get("UnitOptions")}
                                    radioInputGroupId="unit"
                                    onePerLine={false}
                                    changeOperation={LocalOperations.Settings.UnitChange}
                                    selectedItem={this.state.configuration.get("DefaultUnit")}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {this.state.configuration.get("SiteList").length > 0 && (
                    <div className="row">
                        <div className="hidden-sm hidden-xs col-md-2 col-lg-2 text-right">
                            <label className={`form-label bold ${styles.labelPaddingTop}`}>
                                {cacheStore.GetString("ActiveSiteLabel")}
                            </label>
                        </div>
                        <div className={`col-sm-12 col-md-10 col-lg-4 nopadding ${styles.maxWidthCombo}`}>
                            <ComboBoxControl
                                selectedCode={this.state.configuration.get("ActiveSite")}
                                items={Immutable.List(this.state.configuration.get("SiteList").map(x => new ComboBoxDropDownItem(x.Model.Id, x.Model.ClinicalName, 0)))}
                                hasEmptyItem
                                id="_SiteCombobox"
                                onChange={(selectedCode: string) => this.onChangeActiveSite(selectedCode)}
                                maxNumberOptionCharacters={70}
                            />
                        </div>
                    </div>
                )}

                {(this.state.configuration.get("CostCentersList") && this.state.configuration.get("CostCentersList").length > 0) && (
                    <div className="row">
                        <div className="hidden-sm hidden-xs col-md-2 col-lg-2 text-right">
                            <label className={`form-label bold ${styles.labelPaddingTop}`}>
                                {cacheStore.GetString("DepartmentLabel")}
                            </label>
                        </div>
                        <div className={`col-sm-12 col-md-10 col-lg-4 nopadding ${styles.maxWidthCombo}`}>

                            <ComboBoxControl
                                id="_DepartmentCombobox"
                                selectedCode={this.state.configuration.get("ActiveDepartment")}
                                items={Immutable.List(
                                    this.state.configuration.get("CostCentersList").map(
                                        x => new ComboBoxDropDownItem(
                                            x.ID,
                                            PrivateBillCostCenter.GetDisplayName(x),
                                            0
                                        )
                                    )
                                )}
                                hasEmptyItem
                                onChange={(selectedCode: string) => this.onChangeActiveDepartment(selectedCode)}
                                maxNumberOptionCharacters={70}
                            />
                        </div>
                    </div>
                )}

                {!this.state.employee.isClerical() && (
                    <div className="row">
                        <div className="hidden-sm hidden-xs col-md-2 col-lg-2 text-right">
                            <label className={`form-label bold ${styles.labelPaddingTop}`}>{cacheStore.GetString("BillingProvider")}</label>
                        </div>
                        <div className={`col-sm-12 col-md-10 col-lg-4 nopadding ${styles.maxWidthCombo}`}>
                            <div className={styles.valueMarginButton}>
                                <label className="visible-sm visible-xs bold">{cacheStore.GetString("BillingProvider")}</label>
                                <ComboBoxControl
                                    hasEmptyItem
                                    id="BillingProviderComboBox"
                                    items={billingProvidersItems.toImmutable()}
                                    selectedCode={this.state.selectedBillingProviderCode}
                                    onChange={selectedCode => this.handleOnChangeBillingProvider(selectedCode)}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {this.state.externalBillingUser?.BillingUsername && (
                    <div className="row">
                        <div className="hidden-sm hidden-xs col-md-2 col-lg-2 text-right">
                            <label className={`form-label bold ${styles.labelPaddingTop}`}>{cacheStore.GetString("BillingProviderUser")}</label>
                        </div>
                        <div className="col-sm-12 col-md-10 col-lg-8">
                            <div className={`${styles.valueMarginButton} ${styles.valueMediumMarginTop}`}>
                                <label className="visible-sm visible-xs bold">{cacheStore.GetString("BillingProviderUser")}</label>
                                <div>
                                    {ExternalBillingManager.getUserDisplayParts(this.state.externalBillingUser).map(
                                        (displayPart, i) => <span key={`${displayPart}${i}`}>{displayPart}<br /></span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </SettingsTabSection>
        );
    }

    private getEmailModelParams(): IUseEmailUpdateStateParams | null {
        const emailAddressModel = this.state.configuration.get("EmailAddressesModel");

        if (!emailAddressModel)
            return null;

        return {
            emails: emailAddressModel.get("Emails").toArray(),
            isNotifiedByEmail: emailAddressModel.get("IsNotifiedByEmail")
        };
    }

    private renderSignatureViewer(): JSX.Element {
        return (
            <FeatureFlagControl
                flag={cacheStore.ClinicSettings.EnableFixedSignatureRatio}
                activeFeature={(
                    <SignatureViewerThreeToOneRatio
                        signatureSource={SignatureHelper.getImageDataUrlFromBase64(this.state.configuration.get("SignatureByteArray"))}
                        onRemoveSignature={cacheStore.ClinicSettings.EnableQuickDocumentSigning ?
                            null : e => this.clearSignatureImage(e)
                        }
                    />
                )}
                fallbackFeature={(
                    <SignatureViewer
                        signatureSource={SignatureHelper.getImageDataUrlFromBase64(this.state.configuration.get("SignatureByteArray"))}
                        onRemoveSignature={cacheStore.ClinicSettings.EnableQuickDocumentSigning ?
                            null : e => this.clearSignatureImage(e)
                        }
                    />
                )}
            />
        );
    }

    private renderSecuritySection(): JSX.Element {
        let signatureExists = false;

        if (typeof this.state.configuration.get("SignatureByteArray") === "string" &&
            this.state.configuration.get("SignatureByteArray").length > 0) {
            signatureExists = true;
        }

        return (
            <SettingsTabSection
                labelKey="SettingsTabSectionTitle_Security"
                dataCy="settingsTab-securitySection"
            >
                {!cacheStore.ClinicSettings.EnableSSOAuthentication && (
                    <>
                        <div className="row">
                            <div className="hidden-sm hidden-xs col-md-2 col-lg-2 text-right">
                                <label className="form-label bold">{cacheStore.GetString("ConnectionLabel")}</label>
                            </div>
                            <div className="col-sm-12 col-md-10 col-lg-10">
                                <label className="visible-sm visible-xs bold">
                                    {cacheStore.GetString("ConnectionLabel")}
                                </label>

                                {this.state.ui.get("ShowPwdChange") === false &&
                                    <a onClick={this.showPwdChangeControl}>{cacheStore.GetString("ChangePasswordLabel")}</a>
                                }
                            </div>
                        </div>

                        {(!this.state.ui.get("ShowEditEmailAddresses")) && (
                            <div className="row">
                                <div className="hidden-sm hidden-xs col-md-2 col-lg-2 text-right">
                                    <label className="form-label bold">{cacheStore.GetString("PasswordRecoveryLabel")}</label>
                                </div>
                                <div className="col-sm-6 col-md-4 col-lg-4">
                                    <ViewEmailAddressesControl
                                        model={this.state.configuration.get("EmailAddressesModel")}
                                        currentlyProcessing={this.state.ui.get("EmailVerificationInProcess")}
                                        onVerify={(emailAddress, contactPointId, verificationCode) => this.verifyEmail(emailAddress, contactPointId, verificationCode)}
                                        onResend={(emailAddress, contactPointId) => this.resendEmail(emailAddress, contactPointId)}
                                        onChangeIsOkToContactAndNotifiedByEmail={(emails, isNotifiedByEmail) => this.onSaveEmailsInViewMode(emails, isNotifiedByEmail)}
                                        serverError={this.state.configuration.get("EmailVerificationErrorMessage")}
                                    />
                                </div>
                                <div className={`col-sm-4 col-md-6 col-lg-6 ${styles.modifyButtonContainer}`}>
                                    <a onClick={() => this.toggleEditEmail(true)}>
                                        {cacheStore.GetString("ModifyLabel")}
                                    </a>
                                </div>
                            </div>
                        )}

                        {this.state.showPasswordRecoveryPopup && (
                            <RequiredRecoveryEmailController
                                isForLogin={false}
                                emailAddressesModel={this.getEmailModelParams()}
                                sendSaveEmailRequest={async (options, password) => {
                                    const response = await this.employeeActions.saveEmails(options, password);
                                    if (!sessionStore.isRecoveryEmailRequired && response.isSuccess && !response.data.emailAddress)
                                        this.toggleEditEmail(false);
                                    return response;
                                }}
                                onVerifyEmailSuccess={() => this.toggleEditEmail(false)}
                                onClose={() => this.toggleEditEmail(false)}
                                showCloseButton
                            />
                        )}

                        <FeatureFlagControl
                            flag={cacheStore.ClinicSettings.EnableMultifactorAuthenticationModule}
                            activeFeature={(
                                <NewFlowMultifactorAuthenticationSettings
                                    emails={this.state.configuration.get("EmailAddressesModel")?.get("Emails").map(p => p.Address).toArray() ?? []}
                                    lastVerifiedEmail={this.state.configuration.get("LastVerifiedEmail")}
                                    onRequireEmailValidation={() => this.toggleEditEmail(true)}
                                />
                            )}
                            fallbackFeature={(
                                <MultifactorAuthenticationSettings
                                    lastVerifiedEmail={this.state.configuration.get("LastVerifiedEmail")}
                                    currentEmail={this.state.configuration.get("EmailAddressesModel")?.get("Emails")?.find(p => p.IsPreferred)?.Address}
                                    AuthenticatorCodeDeliveryMethod={this.state.configuration.get("SelectedAuthenticatorCodeDeliveryMethod")}
                                    emailAddressesModel={this.state.configuration.get("EmailAddressesModel")}
                                    onAuthenticatorCodeDeliveryMethodChange={method => settingsStore.action.UpdateAuthenticatorCodeDeliveryMethod(method)}
                                    onSaveEmail={(options, password) => this.employeeActions.saveEmails(options, password)}
                                />
                            )}
                        />
                    </>
                )}
                {!this.state.employee.isClerical() && (
                    <div className="row">
                        <div className="hidden-sm hidden-xs col-md-2 col-lg-2 text-right">
                            <label className="form-label bold">{cacheStore.GetString("SignatureLabel")}</label>
                        </div>
                        <div className="col-sm-12 col-md-10 col-lg-10">
                            <label className="visible-sm visible-xs bold">{cacheStore.GetString("SignatureLabel")}</label>
                            <p>
                                {!cacheStore.ClinicSettings.EnableQuickDocumentSigning &&
                                    <span className="fa fa-times" />
                                }
                                <span className={(cacheStore.ClinicSettings.EnableQuickDocumentSigning && !sessionStore.hasPinSet) ? "red-text" : ""}>
                                    {sessionStore.hasPinSet
                                        ? cacheStore.GetString("PinActivatedLabel")
                                        : cacheStore.GetString("PinDeactivatedLabel")
                                    }
                                </span>
                            </p>
                            {this.state.ui.get("ShowPINChange") === false && (!cacheStore.ClinicSettings.EnableQuickDocumentSigning || sessionStore.hasPinSet) && (
                                <a onClick={e => this.showPINChangeControl(e)}>
                                    {cacheStore.GetString(cacheStore.ClinicSettings.EnableQuickDocumentSigning
                                        ? "DocumentSigningPinOptions_EditPinLabel"
                                        : "ChangePINLabel"
                                    )}
                                </a>
                            )}
                            {this.state.ui.get("ShowPINChange") === true && (
                                <PinControl pinRequired={sessionStore.hasPinSet} />
                            )}
                            {(cacheStore.ClinicSettings.EnableQuickDocumentSigning && !signatureExists) &&
                                <span className="show red-text">{cacheStore.GetString("DocumentSigningPinOptions_NoSignatureLabel")}</span>
                            }
                            {signatureExists && this.renderSignatureViewer()}

                            {!cacheStore.ClinicSettings.EnableQuickDocumentSigning && (
                                <FileUploadLite
                                    uploadButtonText={cacheStore.GetString("UploadSignatureLabel")}
                                    onCompleteOperation={LocalOperations.Settings.SignatureUploadComplete}
                                    fileDestinationPath={3}
                                    isSignature
                                    acceptedFormat={SignatureHelper.acceptedMimeTypes}
                                />
                            )}

                            {cacheStore.ClinicSettings.EnableQuickDocumentSigning &&
                                (
                                    <>
                                        <div className="margin-md-top margin-md-bottom">
                                            <label className="form-label bold">{cacheStore.GetString("DocumentSigningPinOptions_PinFrequencyLabel")}</label>
                                        </div>
                                        <div>
                                            <div className="margin-sm-bottom">
                                                <PinFrequencyOptionViewer
                                                    employeeUIModel={this.state.employee}
                                                    loginPinFrequencyModel={this.state.configuration.get("LoginPinFrequency")}
                                                />
                                            </div>
                                            <div>
                                                <ButtonControl
                                                    className="btn btn-default"
                                                    label={cacheStore.GetString("EDITLabel")}
                                                    onClick={() => sessionStore.dispatchMain(LocalOperations.MainComponentViewOperations.DisplaySignatureOptionPopup)}
                                                    dataCy="pin-setting-edit-button"
                                                />
                                            </div>
                                        </div>
                                    </>
                                )
                            }
                        </div>
                    </div>
                )}
            </SettingsTabSection>
        );
    }

    private renderYourPreferences(): JSX.Element {
        const savedLongTermMedicationDuration = this.state.configuration.get("LongTermMedicationDurationInDays");

        return (
            <SettingsTabSection
                labelKey="SettingsTabSectionTitle_YourPreferences"
                dataCy="settingsTab-yourPreferencesSection"
            >
                {cacheStore.ClinicSettings.EnableTelemedPictureInPicture && (
                    <div className="row">
                        <div className="hidden-sm hidden-xs col-md-2 col-lg-2 text-right">
                            <label className="form-label bold">{cacheStore.GetString("TelemedSettingLabel")}</label>
                        </div>
                        <div className="col-sm-12 col-md-10 col-lg-10">
                            <CheckboxLabel
                                id="showTelemedWindowInMylePopup"
                                label={cacheStore.GetString("TelemedShowConferenceWindowInMylePopupLabel")}
                                checked={this.state.configuration.get("IsTelemedWindowInMyleWindow")}
                                onChange={isChecked => {
                                    this.onCheckboxChange(
                                        isChecked,
                                        LocalOperations.Settings.ChangeTelemedWindowOption
                                    );
                                }}
                            />
                        </div>
                    </div>
                )}

                {cacheStore.ClinicSettings.EnableDocumentInAppView && (
                    <div className="row">
                        <div className="hidden-sm hidden-xs col-md-2 text-right">
                            <label className="form-label bold">{cacheStore.GetString("Settings_DefaultDocumentsViewlabel")}</label>
                        </div>
                        <div className="col-sm-12 col-md-10">
                            <label className="visible-xs bold visible-sm">{cacheStore.GetString("Settings_DefaultDocumentsViewlabel")}</label>
                            <RadioButton
                                mainDivCSS="no-side-margin"
                                items={this.state.configuration.get("DocumentView")}
                                radioInputGroupId="documentView"
                                onePerLine
                                changeOperation={LocalOperations.Settings.DocumentViewChange}
                                selectedItem={cacheStore.UserSettings?.UserSettingsConfig?.ShowDocumentInAppView ? "annotateView" : "popoutView"}
                            />
                        </div>
                    </div>
                )}

                {(cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements || cacheStore.ClinicSettings.EnableDefaultAttachmentsOnConsultationRequests) && (
                    <div className="row">
                        <div className="hidden-sm hidden-xs col-md-2 col-lg-2 text-right">
                            <label className="form-label bold">{cacheStore.GetString("Settings_DefaultAttachmentsForConsultRequestsLabel")}</label>
                        </div>
                        <div className="col-sm-12 col-md-10 col-lg-10">
                            <div>
                                <CheckboxLabel
                                    id="ShouldAttachMedicalSummaryWhenCreatingConsultationRequest"
                                    data-cy="ShouldAttachMedicalSummaryWhenCreatingConsultationRequest"
                                    onChange={isChecked => {
                                        this.onCheckboxChange(
                                            isChecked,
                                            LocalOperations.Settings.ChangeShouldAttachMedicalSummaryWhenCreatingConsultationRequest
                                        );
                                    }}
                                    label={cacheStore.GetString("MedicalSummaryLabel")}
                                    checked={this.state.configuration.get("ShouldAttachMedicalSummaryWhenCreatingConsultationRequest")}
                                />
                            </div>

                            <div>
                                <CheckboxLabel
                                    id="ShouldAttachMedicationSummaryWhenCreatingConsultationRequest"
                                    data-cy="ShouldAttachMedicationSummaryWhenCreatingConsultationRequest"
                                    onChange={isChecked => {
                                        this.onCheckboxChange(
                                            isChecked,
                                            LocalOperations.Settings.ChangeShouldAttachMedicationSummaryWhenCreatingConsultationRequest
                                        );
                                    }}
                                    label={cacheStore.GetString("MedicationSummaryLabel")}
                                    checked={this.state.configuration.get("ShouldAttachMedicationSummaryWhenCreatingConsultationRequest")}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {!this.state.employee.isClerical() && (
                    <div className="row">
                        <div className="hidden-sm hidden-xs col-md-2 col-lg-2 text-right">
                            <label className="form-label bold">{cacheStore.GetString("AssistanceLabel")}
                            </label>
                        </div>
                        <div className="col-sm-12 col-md-10 col-lg-10">
                            <div>
                                <label className="visible-sm visible-xs bold">{cacheStore.GetString("AssistanceLabel")}</label>
                                <CheckboxLabel
                                    id="DiadgnosesPatientSummaryMessage"
                                    label={cacheStore.GetString("DiadgnosesPatientSummaryMessage")}
                                    checked={this.state.configuration.get("AreDiagnosticsInPatientSummary")}
                                    onChange={isChecked => {
                                        this.onCheckboxChange(
                                            isChecked,
                                            LocalOperations.Settings.ChangeAutoIncludeDiagnoses
                                        );
                                    }}
                                />
                            </div>
                            <div>
                                <CheckboxLabel
                                    id="ShouldShowAndCalculateMedicationDispensationQuantities"
                                    label={cacheStore.GetString("ShowAndCalculateDispensationQuantitiesLabel")}
                                    checked={this.state.configuration.get("ShouldShowAndCalculateMedicationDispensationQuantities")}
                                    onChange={isChecked => {
                                        this.onCheckboxChange(
                                            isChecked,
                                            LocalOperations.Settings.ShowAndCalculateMedicationDispensationQuantities
                                        );
                                    }}
                                />
                            </div>
                            <div>
                                <CheckboxLabel
                                    id="showDiagnosisCode"
                                    label={cacheStore.GetString("ShowDiagnosisCodeLabel")}
                                    checked={this.state.configuration.get("ShowDiagnosisCode")}
                                    onChange={isChecked => {
                                        this.onCheckboxChange(
                                            isChecked,
                                            LocalOperations.Settings.ShowDiagnosisCode
                                        );
                                    }}
                                />
                            </div>
                            <div>
                                <CheckboxLabel
                                    id="showTimer"
                                    onChange={checked => {
                                        settingsStore.action.UpdateEmployeeSettings(
                                            {
                                                DisableTimers: !checked
                                            },
                                            response => {
                                                if (!response.IsSuccess)
                                                    return;

                                                cacheStore.GetCurrentEmployee().DisableTimers = !checked;
                                                sessionStore.clearCurrentEmployeeCache();
                                                settingsStore.EmitChangeToListeners();
                                            }
                                        );
                                    }}
                                    label={cacheStore.GetString("EnableTimerLabel")}
                                    checked={this.state.employee.DisableTimers != true}
                                />
                            </div>
                            <div>
                                <CheckboxLabel
                                    id="ActivatePharmacotherapeuticAssistant"
                                    label={cacheStore.GetString("ActivatePharmacotherapeuticAssistantLabel")}
                                    checked={this.state.configuration.get("PharmacoTherapeuticActive")}
                                    onChange={isChecked => {
                                        this.onCheckboxChange(
                                            isChecked,
                                            LocalOperations.Settings.ChangePharmacotherapeuticAssistant
                                        );
                                    }}
                                />
                            </div>
                            {this.renderRefillSection()}
                            {cacheStore.ClinicSettings.EnableLongTermMedication && (
                                <>
                                    <div className={styles.longTermMedicationDurationContainer}>
                                        <CheckboxLabel
                                            id="DefaultLongTermMedicationDuration"
                                            onChange={checked => {
                                                this.mutateState(
                                                    s => s.longTermMedicationDefaultRefillNumber = 0,
                                                    () => {
                                                        sessionStore.dispatch(
                                                            LocalOperations.Settings.ChangeDefaultLongTermMedicationDuration,
                                                            {
                                                                value: checked ? this.defaultLongTermMedicationDurationInDays : null,
                                                                longTermRefillValue: 0
                                                            }
                                                        );
                                                    }
                                                );
                                            }}
                                            label={`${cacheStore.GetString("DefaultDurationForLongTermMedicationsLabel")}:`}
                                            checked={savedLongTermMedicationDuration != null}
                                            dataCy="settingsTab-longTermMedicationDurationCheckbox"
                                        />
                                        <TextboxControl
                                            value={String(this.state.longTermMedicationDurationInDays)}
                                            disabled={!savedLongTermMedicationDuration}
                                            debounceMs={500}
                                            isInteger
                                            type="number"
                                            min={1}
                                            max={1000}
                                            className="form-control"
                                            dataCy="settingsTab-longTermMedicationDurationTextbox"
                                            onChange={value => {
                                                this.mutateState(
                                                    s => s.longTermMedicationDurationInDays = Number(value) || null,
                                                    () => {
                                                        if (IsNullOrWhiteSpace(value))
                                                            return;

                                                        sessionStore.dispatch(
                                                            LocalOperations.Settings.ChangeDefaultLongTermMedicationDuration,
                                                            { value: this.state.longTermMedicationDurationInDays }
                                                        );
                                                    }
                                                );
                                            }}
                                        />
                                        <span>{cacheStore.GetString("DaysLabel").toLocaleLowerCase()}</span>
                                    </div>

                                    {savedLongTermMedicationDuration != null && (
                                        <div className={`${styles.longTermMedicationDurationContainer} ${styles.longTermMedicationRefillNumber}`}>
                                            <span>{cacheStore.GetString("DefaultNumberOfRefillLongTermLabel")}: </span>
                                            <TextboxControl
                                                value={this.state.longTermMedicationDefaultRefillNumber > 0 ? String(this.state.longTermMedicationDefaultRefillNumber) : "0"}
                                                disabled={!savedLongTermMedicationDuration}
                                                debounceMs={500}
                                                isInteger
                                                type="number"
                                                min={0}
                                                max={100}
                                                className="form-control"
                                                dataCy="settingsTab-longTermMedicationRefillNumberTextbox"
                                                onChange={value => {
                                                    this.mutateState(
                                                        s => s.longTermMedicationDefaultRefillNumber = Number(value) || null,
                                                        () => {
                                                            if (IsNullOrWhiteSpace(value))
                                                                return;

                                                            sessionStore.dispatch(
                                                                LocalOperations.Settings.ChangeDefaultLongTermMedicationRefillNumber,
                                                                { value: this.state.longTermMedicationDefaultRefillNumber }
                                                            );
                                                        }
                                                    );
                                                }}
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                    </div>
                )}
                {(cacheStore.ClinicSettings.EnableAlertsAndSpecialNeedsSettings && !this.state.employee.isClerical()) && (
                    <div className="row">
                        <div className="hidden-sm hidden-xs col-md-2 col-lg-2 text-right">
                            <label
                                className="form-label bold"
                                data-cy="settings-alertsAndSpecialNeeds"
                            >
                                {cacheStore.GetString("AlertAndSpecialNeedsLowCaseLabel")}
                            </label>
                        </div>
                        <div className="col-sm-12 col-md-10 col-lg-10">
                            <div>
                                <CheckboxLabel
                                    id="ShowAlertsAndSpecialNeedsInPrintedSummary"
                                    label={cacheStore.GetString("Settings_ShowAlertsAndSpecialNeedsInPrintedSummaryLabel")}
                                    checked={this.state.configuration.get("ShowAlertsAndSpecialNeedsInPrintedSummary")}
                                    dataCy="show-alerts-and-special-needs-in-printed-summary"
                                    onChange={isChecked => {
                                        this.onCheckboxChange(
                                            isChecked,
                                            LocalOperations.Settings.ChangeShowAlertsAndSpecialNeedsInPrintedSummary
                                        );
                                    }}
                                />
                            </div>
                            <div>
                                <CheckboxLabel
                                    id="ShowPopupForNewAlertsAndSpecialNeeds"
                                    label={cacheStore.GetString("Settings_ShowPopupForNewAlertsAndSpecialNeedsLabel")}
                                    checked={this.state.configuration.get("ShowPopupForNewAlertsAndSpecialNeeds")}
                                    dataCy="show-popup-for-new-alerts-and-special-needs"
                                    onChange={isChecked => {
                                        this.onCheckboxChange(
                                            isChecked,
                                            LocalOperations.Settings.ChangeShowPopupForNewAlertsAndSpecialNeeds
                                        );
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                )}
                {cacheStore.ClinicSettings.EnableDrugInteractionAlertManagement && (
                    <div className="row">
                        <div className="hidden-sm hidden-xs col-md-2 col-lg-2 text-right">
                            <label className={`form-label bold ${styles.labelPaddingTop}`}>{cacheStore.GetString("Settings_DrugInteractionAlertsLabel")}</label>
                        </div>
                        <div className={`col-sm-12 col-md-10 col-lg-4 nopadding ${styles.maxWidthCombo}`}>
                            <div className={styles.valueMarginButton}>
                                <label className="visible-sm visible-xs bold">{cacheStore.GetString("Settings_DrugInteractionAlertsLabel")}</label>
                                <ComboBoxControl
                                    id="Setting_DrugInteractionAlerts"
                                    items={Immutable.List([
                                        new ComboBoxDropDownItem(`${DrugInteractionAlertsFilterOption.ShowAllAlertLevels}`, cacheStore.GetString("DrugInteractionAlertsFilterOptionLabel_ShowAllAlertLevel")),
                                        new ComboBoxDropDownItem(`${DrugInteractionAlertsFilterOption.ShowAlertLevel1Only}`, cacheStore.GetString("DrugInteractionAlertsFilterOptionLabel_ShowAlertLevel1Only")),
                                        new ComboBoxDropDownItem(`${DrugInteractionAlertsFilterOption.ShowAlertLevel1And2Only}`, cacheStore.GetString("DrugInteractionAlertsFilterOptionLabel_ShowAllAlertLevel1AndLevel2Only"))
                                    ])}
                                    dataCy="settings-drug-interaction-alerts-filter-option"
                                    onChange={option => sessionStore.dispatch(LocalOperations.Settings.ChangeDrugInteractionAlertsFilterOption, { option: parseInt(option) })}
                                    selectedCode={this.state.configuration.get("SelectedDrugInteractionAlertsFilterOption")?.toString()}
                                />
                            </div>
                        </div>
                    </div>
                )}
                {this.renderDynamicPdfSearch()}
                {cacheStore.ClinicSettings.EnableChronicDiseaseManagement && <FlowsheetTemplateSettingsSectionController />}
            </SettingsTabSection>
        );
    }

    private renderRefillSection(): JSX.Element {
        let refillSaveBtn: JSX.Element = null;

        if (this.state.ui.get("ShowSaveRefillNumber")) {
            refillSaveBtn = <a onClick={this.updateRefillNumber.bind(this)}>{cacheStore.GetString("SaveLabel")}</a>;
        }

        return (
            <div>
                <span>{cacheStore.GetString("DefaultRefillsLabel")}: </span>
                <input
                    onPaste={this.cancelPaste.bind(this)}
                    onKeyDown={this.filterKeyForRefills.bind(this)}
                    type="number"
                    min={1}
                    max={100}
                    onChange={this.changeRefillNumber.bind(this)}
                    value={String(this.state.configuration.get("DefaultRefillNumber"))}
                    className={`form-control ${styles.refillTextbox}`}
                />
                {refillSaveBtn}
            </div>
        );
    }

    private renderDynamicPdfSearch(): JSX.Element {
        return (
            <div className="row">
                <div className="hidden-sm hidden-xs col-md-2 col-lg-2 text-right">
                    <label className="form-label bold">{cacheStore.GetString("DynamicPdfSearchTagsLabel")}
                    </label>
                </div>
                <div className="col-sm-12 col-md-10 col-lg-10">

                    <label className="visible-sm visible-xs bold">{cacheStore.GetString("DynamicPdfSearchTagsLabel")}</label>

                    <div className="row native">
                        <div className="col-sm-12 col-md-10 col-lg-5 nopadding">
                            {this.state.ui.get("LoadingTagsCache")
                                ? <LoadingIndicator imagePath={img_loading} />
                                : (
                                    <Autocomplete
                                        selectionOp={LocalOperations.Settings.ChangeTagSelection}
                                        list={this.state.configuration.get("PdfTagsCache")}
                                        selectedValue={Immutable.List(this.state.configuration.get("SelectePDFTagIds"))}
                                        componentId="tags_autocomplete"
                                        autoClose
                                    />
                                )
                            }
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderParentAndResultsManagement(): JSX.Element {
        let clinicianName = this.state.configuration.get("ClinicianNameForOrphanLabs");

        let fromDate = this.state.configuration.get("ClinicianDateFromOrphanLabs");
        let toDate = this.state.configuration.get("ClinicianDateToOrphanLabs");
        let isFromDateValid: boolean = true;
        let isToDateValid: boolean = true;
        if (fromDate != null) {
            if (toDate != null && fromDate > toDate) isFromDateValid = false;
        }
        if (toDate != null) {
            if (fromDate != null && fromDate > toDate) isToDateValid = false;
        }

        let isSelectedClinicianAlreadyAdded = false;
        if (!IsNullOrWhiteSpace(clinicianName)) {
            let clinicians = this.state.configuration.get("ClinicianListForOrphanLabs");
            if (clinicians) {
                isSelectedClinicianAlreadyAdded = clinicians.filter(c => c.Name.trim() === clinicianName.trim()).count() > 0;
            }
        }

        return (
            <SettingsTabSection
                labelKey="SettingsTabSectionTitle_PatientAndResultsManagement"
                dataCy="settingsTab-patientAndResultManagementSection"
            >
                {!this.state.employee.isClerical() && (
                    <div className="row">
                        <div className="hidden-sm hidden-xs col-md-2 col-lg-2 text-right">
                            <label className="form-label bold">{cacheStore.GetString("PatientEnrolmentLabel")}</label>
                        </div>
                        <div className="col-sm-12 col-md-10 col-lg-10">

                            <label className="visible-sm visible-xs bold">{cacheStore.GetString("PatientEnrolmentLabel")}</label>

                            <a onClick={this.showEnrollmentSummary.bind(this)}>{cacheStore.GetString("EnrolmentSummaryLabel")}</a>
                        </div>
                    </div>
                )}
                {!this.state.employee.isClerical() && (
                    <div className="row">
                        <div className="hidden-sm hidden-xs col-md-2 col-lg-2 text-right">
                            <label className={`form-label bold ${styles.labelPaddingTop}`}>{cacheStore.GetString("OrphansLabsLabel")}</label>
                        </div>
                        <div className="col-sm-12 col-md-10 col-lg-10">

                            <label className={`visible-sm visible-xs bold ${styles.labelPaddingTop}`}>{cacheStore.GetString("OrphansLabsLabel")}</label>

                            <div className="myle-row settings">
                                <div className="myle-column search">
                                    <TextboxControl
                                        debounceMs={200}
                                        value={this.state.configuration.get("ClinicianNameForOrphanLabs")}
                                        autoCompleteItems={this.state.itemsOrphanLabs}
                                        onAutoCompleteSelectCallBack={(code: string, model: any, name: string) => {
                                            sessionStore.dispatch(LocalOperations.Settings.OrphanLabsClinicianChange, { value: code, name: name });
                                        }}
                                        onAutoCompleteSelectClearTextBox={false}
                                        placeholder=""
                                        autoComplete="off"
                                        valid={!isSelectedClinicianAlreadyAdded}
                                        id="orphan_labs_clinicians"
                                    />
                                </div>
                                <div className="myle-column date">
                                    <DateSelector
                                        id="labs_from"
                                        disabled={false}
                                        cssClass=""
                                        dateFormat="dddd, MMM dd, yyyy"
                                        minDate={new Date()}
                                        autoComplete="off"
                                        valid={isFromDateValid}
                                        onSelectOp={LocalOperations.Settings.OrphanLabsFromChange}
                                        date={this.state.configuration.get("ClinicianDateFromOrphanLabs")}
                                    />
                                </div>
                                <div className="myle-column date">
                                    <DateSelector
                                        id="labs_to"
                                        disabled={false}
                                        cssClass=""
                                        minDate={new Date()}
                                        autoComplete="off"
                                        valid={isToDateValid}
                                        dateFormat="dddd, MMM dd, yyyy"
                                        onSelectOp={LocalOperations.Settings.OrphanLabsToChange}
                                        date={this.state.configuration.get("ClinicianDateToOrphanLabs")}
                                    />
                                </div>
                                <div className={`myle-column save ${styles.paddingTopSmall}`}>
                                    {(this.state.configuration.get("ClinicianIdForOrphanLabs") !== "") && isFromDateValid && isToDateValid && !isSelectedClinicianAlreadyAdded && (
                                        <a
                                            className={styles.orphanLabList}
                                            onClick={() => this.addClinicianToOrphanLabList()}
                                        >
                                            <strong>{cacheStore.GetString("AddLabel")}</strong>
                                        </a>
                                    )}
                                </div>

                            </div>

                            <div className="myle-row settings">
                                {isSelectedClinicianAlreadyAdded && (
                                    <div className="myle-column search">
                                        <label className="label-error"> {cacheStore.GetString("ClinicianAlreadyAddedError")} </label>
                                    </div>
                                )}
                            </div>

                            <div className="row">
                                {this.state.configuration.get("ClinicianListForOrphanLabs").count() > 0 &&
                                    (
                                        <div className="col-lg-6">
                                            <table className={`table ${styles.marginTopFull}`}>
                                                <tbody>
                                                    {this.state.configuration
                                                        .get("ClinicianListForOrphanLabs")
                                                        .toArray()
                                                        .map((c: OrphanLabsClinician, idx: number) => (
                                                            <tr key={idx + "_ck"}>
                                                                <td className={styles.orphanLabsWidthColumn}>{c.Name} {c.LicenceNumber && (" (" + c.LicenceNumber + ")")}</td>
                                                                <td>
                                                                    {c.DateFrom != null && (
                                                                        <span>{moment(c.DateFrom).format("YYYY/MM/DD")}</span>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    {c.DateTo != null && (
                                                                        <span>{moment(c.DateTo).format("YYYY/MM/DD")}</span>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <button className="button remove" onClick={() => this.removeOrphanLabAssignedClinician(c)}>
                                                                        <span className="glyphicon glyphicon-remove" />
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )
                                }
                            </div>
                        </div>
                    </div>
                )}

                {!this.state.employee.isClerical() && (
                    <div className="row">
                        <div className="hidden-sm hidden-xs col-md-2 col-lg-2 text-right">
                            <label className="form-label bold">{cacheStore.GetString("ResultsLabel")}</label>
                        </div>
                        <div className="col-sm-12 col-md-10 col-lg-10">
                            <label className="visible-sm visible-xs bold  form-label">{cacheStore.GetString("ResultsLabel")}</label>
                            <CheckboxLabel
                                id="ShareResultsD"
                                label={cacheStore.GetString("ShareResultsDLabel")}
                                checked={this.state.configuration.get("AreResultsShared")}
                                onChange={isChecked => {
                                    this.onCheckboxChange(
                                        isChecked,
                                        LocalOperations.Settings.ShareLabResults
                                    );
                                }}
                            />
                        </div>
                    </div>
                )}
                {this.renderPharmaNetCredentialsSection()}
            </SettingsTabSection>
        );
    }

    private renderCommunicationSection(): JSX.Element {
        let activeResponderValue = this.state.configuration.get("IsAutoResponseActive") === true ? "t" : "f";
        let autoResponderForm: JSX.Element = null;

        if (this.state.ui.get("ShowAutoResponderForm")) {
            autoResponderForm = (
                <AutoResponderForm
                    from={this.state.configuration.get("FromVacationDate")}
                    to={this.state.configuration.get("ToVacationDate")}
                    note={this.state.configuration.get("VacationNote")}
                />
            );
        }

        let autoResponderOnOff = (
            <div className={styles.vacationResponseWidth}>
                <RadioButton
                    items={Immutable.List([
                        new KeyValuePair(cacheStore.GetString("ActivatedLabel"), "t"),
                        new KeyValuePair(cacheStore.GetString("DesactivatedLabel"), "f")
                    ])}
                    radioInputGroupId="AutomaticResponse"
                    onePerLine={false}
                    changeOperation={LocalOperations.SettingsUI.AutoRespondersControlShowHide}
                    selectedItem={activeResponderValue}
                    mainDivCSS="no-side-margin"
                />
            </div>
        );

        let autoResponderView = (
            <AutoResponderView
                from={this.state.configuration.get("FromVacationDate")}
                to={this.state.configuration.get("ToVacationDate")}
                note={this.state.configuration.get("VacationNote")}
            />
        );

        if (this.state.configuration.get("IsInVacation")) {
            autoResponderOnOff = null;
        }
        else {
            autoResponderView = null;
        }

        if (this.state.ui.get("ShowAutoResponderForm")) {
            autoResponderView = null;
        }

        return (
            <SettingsTabSection
                labelKey="SettingsTabSectionTitle_Communications"
                dataCy="settingsTab-communicationsSection"
            >
                <div className="row">
                    <div className="hidden-sm hidden-xs col-md-2 col-lg-2 text-right">
                        <label className="form-label bold">
                            {cacheStore.GetString("MessagesSharedWithLabel")}
                        </label>
                    </div>
                    <div className="col-sm-12 col-md-10 col-lg-10">

                        <label className="visible-sm visible-xs bold">
                            {cacheStore.GetString("MessagesSharedWithLabel")}
                        </label>

                        <div>{cacheStore.GetString("StaffLabel")}</div>
                        <div className="row native">
                            <div className="col-sm-12 col-md-10 col-lg-5 nopadding">
                                <PersonSearchAutocompleteCustom
                                    componentId="shared_msg_with_ctrl"
                                    disabled={false}
                                    focusOnLoad={false}
                                    selectionOp={LocalOperations.Settings.SharedMessagesClinicianChange}
                                    clearOp={LocalOperations.Settings.SharedMessagesClinicianChange}
                                    selectedItemList={this.state.configuration.get("CliniciansForMessages")}
                                    searchStrategy={this.clinicianSearchStrategy}
                                    closeDropDownAfterSelection
                                    allowMultipleItemSelection
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="hidden-sm hidden-xs col-md-2 col-lg-2 text-right">
                        <label className="form-label bold">{cacheStore.GetString("TasksSharedWithLabel")}</label>
                    </div>
                    <div className="col-sm-12 col-md-10 col-lg-10">

                        <label className="visible-sm visible-xs bold form-label">{cacheStore.GetString("TasksSharedWithLabel")}</label>

                        <div>{cacheStore.GetString("StaffLabel")}</div>
                        <div className="row native">
                            <div className="col-sm-12 col-md-10 col-lg-5 nopadding">
                                <PersonSearchAutocompleteCustom
                                    componentId="shared_task_with_ctrl"
                                    disabled={false}
                                    focusOnLoad={false}
                                    selectionOp={LocalOperations.Settings.SharedTasksClinicianChange}
                                    clearOp={LocalOperations.Settings.SharedTasksClinicianChange}
                                    selectedItemList={this.state.configuration.get("CliniciansForTasks")}
                                    searchStrategy={this.clinicianSearchStrategy}
                                    closeDropDownAfterSelection
                                    allowMultipleItemSelection
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {cacheStore.ClinicSettings.EnableExternalCommunication && (
                    <div className="row">
                        <div className="hidden-sm hidden-xs col-md-2 col-lg-2 text-right">
                            <label className="form-label bold">{cacheStore.GetString("ExternalCommunicationSharedWithLabel")}</label>
                        </div>
                        <div className="col-sm-12 col-md-10 col-lg-10">
                            <div>{cacheStore.GetString("StaffLabel")}</div>
                            <div className="row native">
                                <div className="col-sm-12 col-md-10 col-lg-5 nopadding">
                                    <PersonSearchAutocompleteCustom
                                        componentId="shared_xcom_sharedwith_ctrl"
                                        disabled={false}
                                        focusOnLoad={false}
                                        selectionOp={LocalOperations.Settings.SharedExternalCommunicationClinicianChange}
                                        clearOp={LocalOperations.Settings.SharedExternalCommunicationClinicianChange}
                                        selectedItemList={this.state.configuration.get("CliniciansForExternalCommunications")}
                                        searchStrategy={this.clinicianSearchStrategy}
                                        closeDropDownAfterSelection
                                        allowMultipleItemSelection
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {cacheStore.ClinicSettings.EnableBidirectionalCommunication && (
                    <>
                        {(cacheStore.ClinicSettings.EnablePatientInitiatedBidirectionalCommunication && !this.state.employee.isClerical()) && (
                            <SettingsTabRow title={cacheStore.GetString("SharedItem_PortalCommunicationsLabel")}>
                                <CheckboxLabel
                                    id="EnablePatientsToInitiatePortalMessagesCheckBox"
                                    onChange={isChecked => {
                                        this.onCheckboxChange(
                                            isChecked,
                                            LocalOperations.Settings.UpdateAllowPatientsToInitiatePortalMessages
                                        );
                                    }}
                                    label={cacheStore.GetString("UserSettings_EnablePatientsToInitiatePortalMessagesLabel")}
                                    checked={this.state.configuration.get("AllowPatientsToInitiatePortalMessages")}
                                />
                            </SettingsTabRow>
                        )}
                        <div className="row">
                            <div className="hidden-sm hidden-xs col-md-2 col-lg-2 text-right">
                                <label className="form-label bold">{cacheStore.GetString("SharedItem_PortalCommunicationsSharedWithLabel")}</label>
                            </div>
                            <div className="col-sm-12 col-md-10 col-lg-10">
                                <div>{cacheStore.GetString("StaffLabel")}</div>
                                <div className="row native">
                                    <div className="col-sm-12 col-md-10 col-lg-5 nopadding">
                                        <PersonSearchAutocompleteCustom
                                            componentId="shared_portal_communication_sharedwith_ctrl"
                                            disabled={false}
                                            focusOnLoad={false}
                                            selectionOp={LocalOperations.Settings.SharedPortalCommunicationClinicianChange}
                                            clearOp={LocalOperations.Settings.SharedPortalCommunicationClinicianChange}
                                            selectedItemList={this.state.configuration.get("CliniciansForPortalCommunications")}
                                            searchStrategy={this.clinicianSearchStrategy}
                                            closeDropDownAfterSelection
                                            allowMultipleItemSelection
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
                <div className="row">
                    <div className="hidden-sm hidden-xs col-md-2 col-lg-2 text-right">
                        <label className="form-label bold">{cacheStore.GetString("VacationResponderLabel")}</label>
                    </div>
                    <div className="col-sm-12 col-md-10 col-lg-10">
                        <div>
                            <label className="visible-sm visible-xs bold">{cacheStore.GetString("VacationResponderLabel")}</label>
                            {autoResponderOnOff}
                        </div>

                        <div>
                            {autoResponderView}
                            {autoResponderForm}
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="hidden-sm hidden-xs col-md-2 col-lg-2 text-right">
                        <label className="form-label bold">{cacheStore.GetString("FaxNumbersLabel")}</label>
                    </div>
                    <div className="col-sm-12 col-md-10 col-lg-10">
                        <FaxAccountNumbersControl
                            faxAccounts={this.state.configuration.get("FaxAccounts")}
                        />
                    </div>
                </div>
            </SettingsTabSection>
        );
    }

    private renderOtherSection(): JSX.Element {
        let employeeModel = (this.state != null && this.state.employee != null) ? this.state.employee.Model : null;

        let syncEmployeeStr = cacheStore.GetString("btnSynchroniserDSQ");
        if (employeeModel != null && employeeModel.FirstNameDSQ != null && employeeModel.LastNameDSQ != null)
            syncEmployeeStr = `${this.state.employee.Model.FirstNameDSQ} ${this.state.employee.Model.LastNameDSQ} ${this.state.employee.Model.LicenceDSQ ? "(" + this.state.employee.Model.LicenceDSQ + ")" : ""}`;

        let linkEditBtn: JSX.Element = null;
        let linkEditControl: JSX.Element = null;
        let usefulLinkUrl: JSX.Element = null;
        let url = this.state.configuration.get("UsefulUrl");
        if (url != null) {
            if ($.trim(url).length > 0) {
                let parts = url.split(";");

                if (parts != null && parts.length > 0) {
                    usefulLinkUrl = (
                        <div>
                            {parts.map((link, idx) => {
                                let l = $.trim(link);
                                let href = l;
                                if (l.length === 0) return null;
                                if (l.indexOf("http") < 0) {
                                    href = "http://" + l;
                                }
                                return (
                                    <div key={idx + "__"}>
                                        <a className={styles.usefulUrlContainer} href={href} target="_blank">{l}</a>
                                    </div>
                                );
                            })}
                        </div>
                    );
                }
                else {
                    usefulLinkUrl = <a href={url} target="_blank">{url}</a>;
                }
            }
        }

        if (this.state.ui.get("ShowUrlEdit") === false) {
            linkEditBtn = <a onClick={this.showLinkEditControl.bind(this)}>{cacheStore.GetString("EDITLabel")}</a>;
        }
        else {
            usefulLinkUrl = null;
            linkEditControl = <UrlEdit value={this.state.configuration.get("UsefulUrl")} />;
        }

        // printing
        let pageOrientation = (this.state.configuration.get("ArePrintLabelsPortrait")) ? "landscape" : "portrait";
        let heightWidthSelectedValue = (this.state.configuration.get("IsLargePrint")) ? "large" : "small";
        let prescriptionPrintType = PrescriptionPrintType.Regular;
        if (this.state.configuration.get("prescriptionPrintType") != null) {
            prescriptionPrintType = this.state.configuration.get("prescriptionPrintType");
        }

        let appointmentPrinterType = this.state.configuration.get("IsNormalConfirmationPrinter") ? "regular_b" : "thermal_b";

        return (
            <SettingsTabSection
                labelKey="SettingsTabSectionTitle_Other"
                dataCy="settingsTab-otherSection"
            >
                <div className="row">
                    <div className="hidden-sm hidden-xs col-md-2 col-lg-2 text-right">
                        <label className="form-label bold">{cacheStore.GetString("PrintingLabel")}</label>
                    </div>
                    <div className="col-sm-12 col-md-10 col-lg-10">

                        <label className="visible-sm visible-xs bold form-label">{cacheStore.GetString("PrintingLabel")}</label>

                        <div className={styles.valueMarginButton}>
                            <div className={styles.printLabelContainer}>
                                <RadioButton
                                    mainDivCSS="settings-indent"
                                    title={cacheStore.GetString("LabelsLabel")}
                                    items={Immutable.List([
                                        new KeyValuePair(
                                            cacheStore.GetString("PortraitLabel"),
                                            "portrait"
                                        ),
                                        new KeyValuePair(
                                            cacheStore.GetString("LandscapeLabel"),
                                            "landscape"
                                        )
                                    ])}
                                    radioInputGroupId="labels"
                                    onePerLine={false}
                                    changeOperation={LocalOperations.Settings.ChangePageOrientation}
                                    selectedItem={pageOrientation}
                                />
                            </div>
                        </div>
                        <div className={styles.valueMarginButton}>
                            <div className={styles.heightSlashContainer}>
                                <RadioButton
                                    mainDivCSS="settings-indent"
                                    title={cacheStore.GetString("HeightSlashWidthLabel")}
                                    items={Immutable.List([
                                        new KeyValuePair(
                                            cacheStore.GetString("ThreePerOneInchesLabel"),
                                            "small"
                                        ),
                                        new KeyValuePair(
                                            cacheStore.GetString("FourPerOnePointFiveInchesLabel"),
                                            "large"
                                        )
                                    ])}
                                    radioInputGroupId="widthHeight"
                                    onePerLine={false}
                                    changeOperation={LocalOperations.Settings.ChangePrintingHeightWidth}
                                    selectedItem={heightWidthSelectedValue}
                                />
                            </div>
                        </div>
                        <div className={styles.valueMarginButton}>
                            <div className={styles.prescriptionContainer}>
                                <RadioButton
                                    mainDivCSS="settings-indent"
                                    title={cacheStore.GetString("PrescriptionLabel")}
                                    items={Immutable.List([
                                        new KeyValuePair(
                                            cacheStore.GetString("RegularPrinterLabel"),
                                            PrescriptionPrintType.Regular
                                        ),
                                        new KeyValuePair(
                                            cacheStore.GetString("ThermicPrinterLabel"),
                                            PrescriptionPrintType.Thermal
                                        ),
                                        new KeyValuePair(
                                            cacheStore.GetString("MexicoAndColombiaStandardsLbl"),
                                            PrescriptionPrintType.Mexico
                                        )
                                    ])}
                                    radioInputGroupId="prescription"
                                    onePerLine={false}
                                    changeOperation={LocalOperations.Settings.ChangePrescriptionPrinter}
                                    selectedItem={prescriptionPrintType}
                                />
                            </div>
                        </div>

                        <div>
                            <div className={styles.appointmentContainer}>
                                <RadioButton
                                    mainDivCSS="settings-indent"
                                    title={cacheStore.GetString("AppointmentConfirmationLabel")}
                                    items={Immutable.List([
                                        new KeyValuePair(
                                            cacheStore.GetString("RegularPrinterLabel"),
                                            "regular_b"
                                        ),
                                        new KeyValuePair(
                                            cacheStore.GetString("ThermicPrinterLabel"),
                                            "thermal_b"
                                        )
                                    ])}
                                    radioInputGroupId="appointment"
                                    onePerLine={false}
                                    changeOperation={LocalOperations.Settings.ChangeAppointmentPrinter}
                                    selectedItem={appointmentPrinterType}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="hidden-sm hidden-xs col-md-2 col-lg-2 text-right">
                        <label className="form-label bold">{cacheStore.GetString("QuickLinkLabel")}</label>
                    </div>
                    <div className="col-sm-12 col-md-10 col-lg-10">

                        <label className="visible-sm visible-xs bold">{cacheStore.GetString("QuickLinkLabel")}</label>

                        <div className={styles.valueMarginButton}>
                            {usefulLinkUrl}
                            {linkEditControl}
                        </div>
                        {linkEditBtn}
                    </div>

                </div>

                {sessionStore.getCurrentEmployee().isActiveSiteQuebec() && (
                    <div className="row">
                        <div className="hidden-sm hidden-xs col-md-2 col-lg-2 text-right">
                            <label className="form-label bold">{cacheStore.GetString("lblThumbprintDSQ")}</label>
                        </div>
                        <div className="col-sm-12 col-md-10 col-lg-10">
                            <div>
                                <ButtonControl
                                    className="btn btn-primary"
                                    label={cacheStore.GetString("btnLocateThumbprint")}
                                    onClick={() => {
                                        settingsStore.action.Execute(
                                            ServerOperations.Server.MSA_LocateCertificateKeys,
                                            {},
                                            null
                                        );
                                    }}
                                />
                            </div>
                            <div>
                                <span>{this.state.employee.Model.ThumbprintKeyDsq}</span>
                            </div>
                        </div>
                    </div>
                )}

                {sessionStore.getCurrentEmployee().isActiveSiteQuebec() && (
                    <div className="row">
                        <div className="hidden-sm hidden-xs col-md-2 col-lg-2 text-right">
                            <label className="form-label bold">{cacheStore.GetString("lblSynchronizationDSQ")}</label>
                        </div>
                        <div className="col-sm-12 col-md-10 col-lg-10">
                            <a onClick={() => this.onSyncEmployeeClick()}>{syncEmployeeStr}</a>
                        </div>
                    </div>
                )}
                {cacheStore.ClinicSettings.IsCallCenterEnabled() && (
                    <div className="row">
                        <div className="hidden-sm hidden-xs col-md-2 col-lg-2 text-right">
                            <label className="form-label bold">{cacheStore.GetString("CallCenterSettingsLabel")}</label>
                        </div>
                        <CallCenterSettings />
                    </div>
                )}

                {cacheStore.ClinicSettings.EnableAIScribe && (
                    <AIScribeUserLinkStatus
                        user={this.state.aiScribeModel.get("AIScribeLinkedUser")}
                        showLink={!this.state.aiScribeModel.get("ShowAIScribePopup")}
                        onLinkClicked={() => sessionStore.dispatch(LocalOperations.AIScribe.ShowAIScribeLinkPopup, {})}
                        onUnlinkClicked={() => sessionStore.dispatch(LocalOperations.AIScribe.ShowAIScribeUnlinkCarewayPopup, {})}
                    />
                )}
            </SettingsTabSection>
        );
    }

    private getBillingComboBoxDropDownItem(value: BillingProvider, position: number): ComboBoxDropDownItem {
        return new ComboBoxDropDownItem(
            EnumHelper.getName(BillingProvider, value),
            BillingProviderHelper.getProviderDisplayName(value),
            position
        );
    }

    private renderPharmaNetCredentialsSection(): JSX.Element {
        if (!cacheStore.hasPharmaNetAccess())
            return null;

        return (
            <div className="row">
                <div className="hidden-sm hidden-xs col-xs-2 text-right">
                    <label className="form-label bold">{cacheStore.GetString("PharmaNetCredentialsLabel")}</label>
                </div>
                <div className="col-xs-4">
                    <label>{sessionStore.pharmaNetUsername}</label>
                </div>
                <div className="col-xs-6">
                    <a onClick={() => this.togglePharmaNetCredentials()}>
                        {cacheStore.GetString(sessionStore.hasPharmaNetCredentials()
                            ? "ModifyLabel"
                            : "AddLabel"
                        )}
                    </a>
                </div>
            </div>
        );
    }

    private renderPharmaNetCredentialsPopup(): JSX.Element {
        if (!this.state.showPharmaNetCredentials)
            return null;

        return (
            <PharmaNetCredentials
                onClose={() => this.togglePharmaNetCredentials()}
                context={PharmaNetContext.Settings}
            />
        );
    }

    private changeListener(): void {
        this.mutateState(s => {
            s.employee = sessionStore.getCurrentEmployee();
            s.configuration = settingsStore.configuration;
            s.ui = settingsStore.uiLogic;
            s.longTermMedicationDurationInDays = settingsStore.configuration.get("LongTermMedicationDurationInDays") || this.defaultLongTermMedicationDurationInDays;
        });
    }

    private showPwdChangeControl(): void {
        sessionStore.dispatch(LocalOperations.SettingsUI.ShowPwdChange);
    }

    private changeRefillNumber(e): void {
        let val = e.target.value;
        sessionStore.dispatch(LocalOperations.Settings.ChangeDefaultRefillNumber, { value: val });
    }

    private updateRefillNumber(e): void {
        e.preventDefault();
        sessionStore.dispatch(LocalOperations.Settings.SaveRefillNumber);
    }

    private filterKeyForRefills(e: React.KeyboardEvent): void {
        if (e.keyCode === 190 || e.keyCode == 110) e.preventDefault();
    }

    private cancelPaste(e: React.ClipboardEvent<HTMLInputElement>): void {
        e.preventDefault();
    }

    private showLinkEditControl(): void {
        sessionStore.dispatch(LocalOperations.SettingsUI.ShowUrlEditControl);
    }

    private clearSignatureImage(e: React.MouseEvent<HTMLSpanElement>): void {
        e.preventDefault();
        if (confirm("Remove singature image?")) {
            sessionStore.dispatch(LocalOperations.Settings.ClearSignatureImage);
        }
    }

    private showEnrollmentSummary(e): void {
        e.preventDefault();
        sessionStore.dispatch(LocalOperations.SettingsUI.ToggleEnrollmentSummary);
    }

    private showPINChangeControl(e): void {
        e.preventDefault();
        sessionStore.dispatch(LocalOperations.SettingsUI.ShowPINChange);
    }

    private addClinicianToOrphanLabList(): void {
        sessionStore.dispatch(LocalOperations.Settings.AddClinicianToOrphanLabsList);
    }

    private removeOrphanLabAssignedClinician(c: OrphanLabsClinician): void {
        sessionStore.dispatch(LocalOperations.Settings.RemoveClinicianFromOrphanLabsList, { value: c });
    }

    private onSyncEmployeeClick(): void {
        sessionStore.dispatchMain(LocalOperations.MainComponentViewOperations.DisplayDSQEmployeePopup);
    }

    private onSaveEmailsInViewMode(emails: Email[], isNotifiedByEmail: boolean): void {
        this.mutateState(s => {
            s.emailsToSave = emails;
            s.isNotifiedByEmail = isNotifiedByEmail;
        });

        sessionStore.dispatch(
            LocalOperations.Settings.SaveEmployeeEmails,
            {
                emails: emails,
                isNotifiedByEmail: isNotifiedByEmail
            }
        );
    }

    private showOverlay(): boolean {
        return this.state.ui.get("ShowPwdChange");
    }

    private verifyEmail(emailAddress: string, contactPointId: string, verificationCode: string): void {
        Dispatcher.dispatch(
            LocalOperations.Settings.VerifyEmployeeEmail,
            {
                emailAddress,
                contactPointId,
                verificationCode
            }
        );
    }

    private resendEmail(emailAddress: string, contactPointId: string): void {
        Dispatcher.dispatch(
            LocalOperations.Settings.ResendEmployeeEmailVerificationCode,
            {
                emailAddress,
                contactPointId
            }
        );
    }

    private onChangeActiveSite(selectedCode: string): void {
        sessionStore.dispatch(LocalOperations.Settings.ActiveSiteChange, { selectedCode: selectedCode });
    }

    private onChangeActiveDepartment(selectedCode: string): void {
        sessionStore.dispatch(LocalOperations.Settings.ActiveDepartmentChange, { selectedCode: selectedCode });
    }

    private togglePharmaNetCredentials(): void {
        this.mutateState(s => s.showPharmaNetCredentials = !s.showPharmaNetCredentials);
    }

    private toggleEditEmail(isEdit: boolean): void {
        this.mutateState(s => s.showPasswordRecoveryPopup = isEdit);
        if (!isEdit)
            sessionStore.dispatch(LocalOperations.Settings.LoadEmployeeEmails);
    }

    private handleOnChangeBillingProvider(selectedCode: string): void {
        const hasEmail = this.getEmailModelParams().emails !== undefined && this.getEmailModelParams().emails?.length > 0;
        if (selectedCode == EnumHelper.getName(BillingProvider, BillingProvider.ClinicAid) && !hasEmail) {
            return sessionStore.showPopup({
                title: cacheStore.GetString("ErrorLabel"),
                content: cacheStore.GetString("BillingProvider_UserEmailRequiredErrorMessage")
            });
        }

        settingsStore.action.UpdateEmployeeSettings(
            {
                BillingProvider: selectedCode || ""
            },
            response => {
                if (!response.IsSuccess) {
                    if (response.Errors && response.Errors.length > 0) {
                        return sessionStore.showPopup({
                            title: cacheStore.GetString("ErrorLabel"),
                            content: cacheStore.GetString(response.Errors[0].Message)
                        });
                    }

                    return;
                }

                const currentEmployee = sessionStore.getCurrentEmployee();
                const resolvedProvider = BillingProviderHelper.resolveBillingProvider(selectedCode);

                if (currentEmployee.getBillingProviderResolved() !== resolvedProvider) {
                    currentEmployee.setExternalBillingUser(null);
                }

                currentEmployee.Model.BillingProvider = selectedCode;

                sessionStore.rebindCurrentEmployee(
                    EmployeeUIModel.fromCompositeObject(currentEmployee.Model)
                );

                this.mutateState(s => {
                    if (BillingProviderHelper.resolveBillingProvider(s.selectedBillingProviderCode) !== resolvedProvider) {
                        s.externalBillingUser = null;
                    }

                    s.selectedBillingProviderCode = selectedCode;
                });
            }
        );
    }

    private onCheckboxChange(
        checked: boolean,
        operation: number
    ): void {
        sessionStore.dispatch(operation, { target: CheckboxTarget.Unassigned, checked });
    }
}

export { SettingsTab };
